import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Trophy, Search, RefreshCw, Download, ChevronDown, ChevronUp, TrendingUp, TrendingDown, AlertTriangle, Layers, Lock, X,
} from 'lucide-react';

import LiveIndicator from '../ui/LiveIndicator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => v == null || Number.isNaN(v) ? '—' : `$${Math.round(v).toLocaleString('en-AU')}`;
const fmtK = (v) => v == null || Number.isNaN(v) ? '—' : Math.abs(v) >= 1_000_000
  ? `$${(v / 1_000_000).toFixed(2)}M`
  : `$${Math.round(v / 1000)}k`;
const pct = (v, digits = 1) => v == null || Number.isNaN(v) ? '—' : `${v.toFixed(digits)}%`;

// Revenue Detail — a single searchable, sortable, rankable table of every
// plan in the selected FY.  Focused on revenue streams + gross margin %,
// with per-plan YoY and budget-variance so the user can spot outliers fast.
const ClientLadder = () => {
  const [years, setYears] = useState([]);
  const [selectedFy, setSelectedFy] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [managerFilter, setManagerFilter] = useState('all');
  const [tlFilter, setTlFilter] = useState('all');
  const [sortKey, setSortKey] = useState('totalActual');
  const [sortDir, setSortDir] = useState('desc');
  // Group rows by OC base plan (strip trailing /N) — client-side aggregation
  // so we reuse the same data payload as the per-plan view.
  const [groupByBase, setGroupByBase] = useState(false);

  const loadYears = useCallback(async () => {
    const res = await axios.get(`${API}/revenue-tracker/years`);
    setYears(res.data.years);
    if (!selectedFy && res.data.years.length) setSelectedFy(res.data.years[0].fy);
  }, [selectedFy]);

  const loadLadder = useCallback(async (fy) => {
    if (!fy) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/client-ladder?fy=${fy}`);
      setData(res.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadYears(); }, [loadYears]);
  useEffect(() => { if (selectedFy) loadLadder(selectedFy); }, [selectedFy, loadLadder]);

  // Optimistic override save — patches the local rows immediately then
  // re-fetches to pick up the audit timestamp.  Enter/Tab focus chaining
  // happens in the cell component; we just handle persistence here.
  const saveLotOverride = useCallback(async (planNumber, newLots) => {
    if (!data) return;
    try {
      await axios.post(`${API}/lot-overrides`, {
        planNumber,
        lots: Number(newLots),
        note: '',
        updatedBy: 'user',
      });
      loadLadder(selectedFy);
    } catch (err) {
      console.error('Failed to save lot override:', err);
      alert('Failed to save override — check console.');
    }
  }, [data, selectedFy, loadLadder]);

  const clearLotOverride = useCallback(async (planNumber) => {
    try {
      await axios.delete(`${API}/lot-overrides`, { params: { planNumber } });
      loadLadder(selectedFy);
    } catch (err) {
      console.error('Failed to clear override:', err);
    }
  }, [selectedFy, loadLadder]);

  const managers = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.rows.map(r => r.manager).filter(Boolean))).sort();
  }, [data]);
  const tls = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.rows.map(r => r.teamLeader).filter(Boolean))).sort();
  }, [data]);

  const filteredSorted = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    // Start from either the per-plan rows or a group roll-up.
    const source = groupByBase ? aggregateByBase(data.rows, data.streamKeys) : data.rows;
    let rows = source.filter(r => {
      if (managerFilter !== 'all' && r.manager !== managerFilter && !r.managers?.includes?.(managerFilter)) return false;
      if (tlFilter !== 'all' && r.teamLeader !== tlFilter && !r.teamLeaders?.includes?.(tlFilter)) return false;
      if (!q) return true;
      return (r.planNumber || '').toLowerCase().includes(q)
        || (r.propertyName || '').toLowerCase().includes(q)
        || (r.manager || '').toLowerCase().includes(q)
        || (r.teamLeader || '').toLowerCase().includes(q);
    });
    // Streams accessors: allow sorting by stream key directly.
    rows = rows.slice().sort((a, b) => {
      const av = getSortVal(a, sortKey);
      const bv = getSortVal(b, sortKey);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return rows;
  }, [data, search, managerFilter, tlFilter, sortKey, sortDir, groupByBase]);

  // Column totals — recomputed against the currently filtered rows so
  // narrowing the search / manager filter updates the header sums live.
  const totals = useMemo(() => {
    if (!data) return null;
    const t = {
      count: filteredSorted.length,
      occupiableLots: 0,
      totalActual: 0,
      totalForecast: 0,
      totalBudget: 0,
      grossMarginSum: 0,
      grossMarginKnown: false,
      workloadCost: 0,
      streams: Object.fromEntries(data.streamKeys.map(k => [k, 0])),
    };
    for (const r of filteredSorted) {
      t.occupiableLots += r.occupiableLots || 0;
      t.totalActual    += r.totalActual   || 0;
      t.totalForecast  += r.totalForecast || 0;
      t.totalBudget    += r.totalBudget   || 0;
      t.workloadCost   += r.workloadCost  || 0;
      if (r.grossMargin != null) {
        t.grossMarginSum += r.grossMargin;
        t.grossMarginKnown = true;
      }
      for (const k of data.streamKeys) {
        t.streams[k] += r.streams?.[k] || 0;
      }
    }
    t.budgetVariance    = t.totalBudget ? t.totalActual - t.totalBudget : null;
    t.budgetVariancePct = t.totalBudget ? (t.budgetVariance / t.totalBudget) * 100 : null;
    // YoY needs the prior FY total for the same subset — use the doc-level number.
    t.priorFyActual     = data.totals?.priorFy || 0;
    t.yoyDelta          = t.priorFyActual ? t.totalActual - t.priorFyActual : null;
    t.yoyDeltaPct       = t.priorFyActual ? (t.yoyDelta / t.priorFyActual) * 100 : null;
    t.grossMargin       = t.grossMarginKnown ? t.grossMarginSum : null;
    t.grossMarginPct    = t.grossMarginKnown && t.totalActual ? (t.grossMarginSum / t.totalActual) * 100 : null;
    t.revenuePerLot     = t.occupiableLots ? t.totalActual / t.occupiableLots : 0;
    return t;
  }, [filteredSorted, data]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const exportCSV = () => {
    if (!data) return;
    const streamKeys = data.streamKeys;
    const header = [
      'Plan', 'Property', 'Manager', 'Team Leader', 'Lots',
      'Total Actual', 'Total Forecast', 'Total Budget',
      'Budget Variance $', 'Budget Variance %', 'YoY Delta $', 'YoY Delta %',
      'Gross Margin $', 'Gross Margin %', 'Revenue / Lot', 'Workload Cost',
      ...streamKeys.map(k => data.streamLabels[k]),
    ];
    const rows = filteredSorted.map(r => [
      r.planNumber, r.propertyName, r.manager, r.teamLeader, r.occupiableLots,
      r.totalActual, r.totalForecast, r.totalBudget,
      r.budgetVariance ?? '', r.budgetVariancePct ?? '', r.yoyDelta ?? '', r.yoyDeltaPct ?? '',
      r.grossMargin ?? '', r.grossMarginPct ?? '', r.revenuePerLot, r.workloadCost,
      ...streamKeys.map(k => r.streams?.[k] || 0),
    ]);
    const csv = [header, ...rows]
      .map(row => row.map(cell => {
        if (cell == null) return '';
        const s = String(cell);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `client-ladder-${data.fy}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !data) {
    return (
      <div className="p-8 text-center text-[#737373]" data-testid="client-ladder-loading">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3 text-[#A3A3A3]" />
        Loading client ladder…
      </div>
    );
  }

  if (!years.length || !data) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center bg-white border border-dashed border-[#E5E5E5] rounded-[14px]" data-testid="client-ladder-empty">
        <Trophy className="w-12 h-12 text-[#A3A3A3] mx-auto mb-4" />
        <h2 className="text-xl font-bold text-[#171717] mb-2">Revenue Detail</h2>
        <p className="text-sm text-[#525252]">Upload a Revenue Report workbook via <em>Revenue Tracker</em> to populate the table.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-ladder">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-[10px] p-6 bg-white border border-[#E5E5E5]" style={{ boxShadow: 'var(--card-shadow)' }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#171717] flex items-center justify-center">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A3A3A3] mb-1">Per-Plan Revenue Detail</p>
              <h1 className="text-xl font-bold text-[#171717] font-heading tracking-tight">Revenue Detail</h1>
              <p className="text-sm text-[#737373] mt-1 max-w-2xl leading-relaxed">
                Rank every OC plan by any revenue stream, budget variance, YoY delta or gross margin. Fully searchable — excluded (soft-deleted) plans are hidden.
              </p>
              <LiveIndicator className="mt-2" label={data.fy} />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <select
                data-testid="ladder-fy-selector"
                value={selectedFy || ''}
                onChange={(e) => setSelectedFy(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] text-[#171717] text-sm font-semibold focus:outline-none focus:border-[#171717]"
              >
                {years.map(y => <option key={y.fy} value={y.fy}>{y.fy}</option>)}
              </select>
              <ChevronDown className="w-3.5 h-3.5 text-[#A3A3A3] absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            <button
              onClick={exportCSV}
              data-testid="ladder-export-csv"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-[#E5E5E5] text-sm font-semibold text-[#525252] hover:border-[#171717]"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </div>

        {/* Summary strip */}
        <div className="mt-5 pt-4 border-t border-[#E5E5E5] grid grid-cols-2 md:grid-cols-6 gap-4" data-testid="ladder-summary">
          <SummaryCell label="Plans" value={data.planCount.toLocaleString()} sub={data.excludedPlanCount ? `${data.excludedPlanCount} excluded` : 'all included'} />
          <SummaryCell label="Total Lots" value={(data.rows.reduce((s, r) => s + (r.occupiableLots || 0), 0)).toLocaleString()} sub="Lots Under Management" />
          <SummaryCell label="Actual" value={fmtK(data.totals.actual)} sub="year-to-date" />
          <SummaryCell label="Forecast" value={fmtK(data.totals.forecast)} sub={`for ${data.fy}`} />
          <SummaryCell label="Budget" value={fmtK(data.totals.budget)} sub={data.totals.budget ? 'prior FY × uplift' : 'no prior FY'} />
          <SummaryCell label="Prior FY Actual" value={fmtK(data.totals.priorFy)} sub="for YoY compare" />
        </div>
      </div>

      {/* Filters */}
      {data.unmappedPlanCount > 0 && (
        <LadderUnmappedWarning
          count={data.unmappedPlanCount}
          revenue={data.unmappedRevenue}
          plans={data.unmappedPlans || []}
          fy={data.fy}
        />
      )}

      <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-4 flex items-center gap-3 flex-wrap" style={{ boxShadow: 'var(--card-shadow)' }}>
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-3.5 h-3.5 text-[#A3A3A3] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="ladder-search"
            placeholder="Search plan number, property, manager or TL"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#FAFAFA] border border-[#E5E5E5] text-[#171717] text-sm focus:outline-none focus:border-[#171717]"
          />
        </div>
        <select
          data-testid="ladder-manager-filter"
          value={managerFilter}
          onChange={(e) => setManagerFilter(e.target.value)}
          className="px-3 py-2 rounded-lg bg-[#FAFAFA] border border-[#E5E5E5] text-[#171717] text-sm focus:outline-none focus:border-[#171717]"
        >
          <option value="all">All managers</option>
          {managers.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select
          data-testid="ladder-tl-filter"
          value={tlFilter}
          onChange={(e) => setTlFilter(e.target.value)}
          className="px-3 py-2 rounded-lg bg-[#FAFAFA] border border-[#E5E5E5] text-[#171717] text-sm focus:outline-none focus:border-[#171717]"
        >
          <option value="all">All team leaders</option>
          {tls.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <p className="text-[11px] text-[#737373] ml-auto">
          Showing <span className="font-semibold text-[#171717]">{filteredSorted.length}</span> of {groupByBase ? aggregateByBase(data.rows, data.streamKeys).length : data.rows.length}
          {groupByBase && ' groups'}
        </p>
        <button
          data-testid="ladder-group-toggle"
          onClick={() => setGroupByBase(g => !g)}
          className={`text-[11px] font-semibold px-3 py-1.5 rounded-lg border transition-colors flex items-center gap-1.5
            ${groupByBase
              ? 'bg-[#171717] text-white border-[#171717]'
              : 'bg-white text-[#525252] border-[#E5E5E5] hover:border-[#171717]'}`}
        >
          <Layers className="w-3 h-3" />
          {groupByBase ? 'Grouped by OC Plan' : 'Group by OC Plan'}
        </button>
      </div>

      {/* Ladder table */}
      <div className="bg-white border border-[#E5E5E5] rounded-[10px]" style={{ boxShadow: 'var(--card-shadow)' }}>
        <div className="overflow-x-auto max-h-[720px]" data-testid="ladder-table">
          <table className="w-full text-xs">
            <thead className="bg-[#FAFAFA] sticky top-0 z-10">
              <tr>
                <ThCell testId="th-rank" label="#" />
                <SortableTh testId="th-plan" label={groupByBase ? 'OC Plan' : 'Plan'} k="planNumber" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="left" />
                <SortableTh testId="th-property" label={groupByBase ? 'Properties' : 'Property'} k="propertyName" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="left" />
                <SortableTh testId="th-manager" label={groupByBase ? 'Managers' : 'Manager'} k="manager" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="left" />
                {groupByBase && <SortableTh testId="th-plan-count" label="Plans" k="planCount" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />}
                <SortableTh testId="th-lots" label="Lots" k="occupiableLots" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                <SortableTh testId="th-total" label="Total" k="totalActual" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                <SortableTh testId="th-forecast" label="Forecast" k="totalForecast" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                <SortableTh testId="th-budget" label="Budget" k="totalBudget" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                <SortableTh testId="th-budget-var" label="vs Budget" k="budgetVariancePct" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                <SortableTh testId="th-yoy" label="YoY %" k="yoyDeltaPct" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                <SortableTh testId="th-margin-dollar" label="Gross Margin $" k="grossMargin" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                <SortableTh testId="th-margin-pct" label="Gross Margin %" k="grossMarginPct" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                <SortableTh testId="th-rev-lot" label="Rev / Lot" k="revenuePerLot" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
                {data.streamKeys.map(k => (
                  <SortableTh
                    key={k}
                    testId={`th-${k}`}
                    label={data.streamLabels[k].replace('OC ', '').replace(' Revenue', '')}
                    k={`stream:${k}`}
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onClick={toggleSort}
                    align="right"
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Sticky totals row — sums live-filter subset */}
              {totals && (
                <tr data-testid="ladder-totals-row" className="bg-[#171717] text-white sticky top-[36px] z-[5]">
                  <td className="px-2 py-2 text-right font-mono text-[10px] opacity-60">Σ</td>
                  <td className="px-3 py-2 font-bold text-[11px] uppercase tracking-wider">Total ({totals.count})</td>
                  <td className="px-3 py-2 opacity-60 text-[11px]">—</td>
                  <td className="px-3 py-2 opacity-60 text-[11px]">—</td>
                  {groupByBase && <td className="px-3 py-2 opacity-60 text-[11px] text-right">—</td>}
                  <td className="px-3 py-2 text-right font-mono font-bold text-[11px]">{totals.occupiableLots ? totals.occupiableLots.toLocaleString() : '—'}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-[11px]">{fmt(totals.totalActual)}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-[11px] text-emerald-300">{fmt(totals.totalForecast)}</td>
                  <td className="px-3 py-2 text-right font-mono text-[11px] opacity-80">{totals.totalBudget ? fmt(totals.totalBudget) : '—'}</td>
                  <td className="px-3 py-2 text-right font-mono text-[11px]">
                    {totals.budgetVariancePct == null
                      ? <span className="opacity-40">—</span>
                      : <span className={totals.budgetVariancePct >= 0 ? 'text-emerald-300' : 'text-rose-300'}>
                          {totals.budgetVariancePct >= 0 ? '+' : ''}{totals.budgetVariancePct.toFixed(1)}%
                        </span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-[11px]">
                    {totals.yoyDeltaPct == null
                      ? <span className="opacity-40">—</span>
                      : <span className={totals.yoyDeltaPct >= 0 ? 'text-emerald-300' : 'text-rose-300'}>
                          {totals.yoyDeltaPct >= 0 ? '+' : ''}{totals.yoyDeltaPct.toFixed(1)}%
                        </span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-[11px]">{totals.grossMargin != null ? fmt(totals.grossMargin) : '—'}</td>
                  <td className="px-3 py-2 text-right font-mono text-[11px]">
                    {totals.grossMarginPct != null ? `${totals.grossMarginPct.toFixed(1)}%` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-[11px] opacity-80">{totals.revenuePerLot ? fmt(totals.revenuePerLot) : '—'}</td>
                  {data.streamKeys.map(k => (
                    <td key={k} className="px-3 py-2 text-right font-mono text-[11px] opacity-80">
                      {totals.streams[k] ? Math.round(totals.streams[k]).toLocaleString() : '·'}
                    </td>
                  ))}
                </tr>
              )}
              {filteredSorted.map((r, i) => (
                <tr key={`${r.planNumber}-${i}`} className="border-t border-[#F0F0F0] hover:bg-[#FAFAFA]/60">
                  <td className="px-2 py-1.5 text-right text-[#A3A3A3] font-mono text-[10px]">{i + 1}</td>
                  <td className="px-3 py-1.5 font-mono font-semibold text-[#171717]" data-testid={`row-plan-${r.planNumber}`}>{r.planNumber}</td>
                  <td className="px-3 py-1.5 text-[#525252] max-w-[220px] truncate" title={r.propertyName}>{r.propertyName || '—'}</td>
                  <td className="px-3 py-1.5 text-[#525252] whitespace-nowrap">{r.manager || '—'}</td>
                  {groupByBase && (
                    <td className="px-3 py-1.5 text-right">
                      <span className="inline-block px-1.5 py-0.5 rounded-full bg-[#171717]/5 text-[#171717] text-[10px] font-semibold font-mono">
                        {r.planCount}
                      </span>
                    </td>
                  )}
                  {groupByBase ? (
                    <td className="px-3 py-1.5 text-right text-[#525252]">{r.occupiableLots || '—'}</td>
                  ) : (
                    <LotsCell
                      row={r}
                      rowIndex={i}
                      onSave={(newVal) => saveLotOverride(r.planNumber, newVal)}
                      onClear={() => clearLotOverride(r.planNumber)}
                    />
                  )}
                  <td className="px-3 py-1.5 text-right font-mono font-semibold text-[#171717]">{fmt(r.totalActual)}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-semibold text-emerald-700">{fmt(r.totalForecast)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-[#525252]">{r.totalBudget ? fmt(r.totalBudget) : '—'}</td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    <VarianceChip pctVal={r.budgetVariancePct} />
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    <VarianceChip pctVal={r.yoyDeltaPct} />
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-[#525252]">{r.grossMargin != null ? fmt(r.grossMargin) : '—'}</td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    <MarginPctChip pctVal={r.grossMarginPct} />
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-[#525252]">{r.revenuePerLot ? fmt(r.revenuePerLot) : '—'}</td>
                  {data.streamKeys.map(k => (
                    <td key={k} className="px-3 py-1.5 text-right font-mono text-[#525252]">{r.streams?.[k] ? Math.round(r.streams[k]).toLocaleString() : '·'}</td>
                  ))}
                </tr>
              ))}
              {filteredSorted.length === 0 && (
                <tr>
                  <td colSpan={(groupByBase ? 14 : 13) + data.streamKeys.length} className="py-8 text-center text-[#A3A3A3] text-xs italic">No plans match the current filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

function getSortVal(row, key) {
  if (key.startsWith('stream:')) {
    const s = key.slice(7);
    return row.streams?.[s] || 0;
  }
  return row[key];
}

// Strip trailing "/N" (e.g. 724927D/1 → 724927D) to derive the OC Plan Group key.
// If the plan number doesn't match the pattern, use it as-is (single-plan group).
function basePlanKey(planNumber) {
  if (!planNumber) return '';
  const m = String(planNumber).match(/^(.+?)\/\d+$/);
  return m ? m[1] : planNumber;
}

// Aggregate per-plan rows to one row per OC base plan.  Sums numeric fields,
// dedupes property + manager names, recomputes weighted margin % and per-lot.
function aggregateByBase(rows, streamKeys) {
  const groups = new Map();
  for (const r of rows) {
    const key = basePlanKey(r.planNumber);
    if (!groups.has(key)) {
      groups.set(key, {
        planNumber: key,
        planCount: 0,
        properties: new Set(),
        managers: new Set(),
        teamLeaders: new Set(),
        occupiableLots: 0,
        totalActual: 0,
        totalForecast: 0,
        totalBudget: 0,
        priorFyActual: 0,
        workloadCost: 0,
        grossMargin: 0,
        grossMarginKnown: false,
        streams: Object.fromEntries(streamKeys.map(k => [k, 0])),
      });
    }
    const g = groups.get(key);
    g.planCount += 1;
    if (r.propertyName) g.properties.add(r.propertyName);
    if (r.manager) g.managers.add(r.manager);
    if (r.teamLeader) g.teamLeaders.add(r.teamLeader);
    g.occupiableLots += r.occupiableLots || 0;
    g.totalActual   += r.totalActual   || 0;
    g.totalForecast += r.totalForecast || 0;
    g.totalBudget   += r.totalBudget   || 0;
    g.priorFyActual += r.priorFyActual || 0;
    g.workloadCost  += r.workloadCost  || 0;
    if (r.grossMargin != null) {
      g.grossMargin += r.grossMargin;
      g.grossMarginKnown = true;
    }
    for (const k of streamKeys) {
      g.streams[k] += r.streams?.[k] || 0;
    }
  }
  // Finalise each group.
  return Array.from(groups.values()).map(g => {
    const propertyName = Array.from(g.properties).slice(0, 2).join(' · ') + (g.properties.size > 2 ? ` +${g.properties.size - 2}` : '');
    const manager     = Array.from(g.managers).slice(0, 2).join(' · ') + (g.managers.size > 2 ? ` +${g.managers.size - 2}` : '');
    const teamLeader  = Array.from(g.teamLeaders).slice(0, 2).join(' · ');
    const budgetVariance    = g.totalBudget ? g.totalActual - g.totalBudget : null;
    const budgetVariancePct = g.totalBudget ? (budgetVariance / g.totalBudget) * 100 : null;
    const yoyDelta          = g.priorFyActual ? g.totalActual - g.priorFyActual : null;
    const yoyDeltaPct       = g.priorFyActual ? (yoyDelta / g.priorFyActual) * 100 : null;
    const revenuePerLot     = g.occupiableLots ? g.totalActual / g.occupiableLots : 0;
    const grossMarginPct    = g.grossMarginKnown && g.totalActual ? (g.grossMargin / g.totalActual) * 100 : null;
    return {
      ...g,
      planCount: g.planCount,
      propertyName: propertyName || '—',
      manager: manager || '—',
      teamLeader,
      managers: Array.from(g.managers),
      teamLeaders: Array.from(g.teamLeaders),
      budgetVariance,
      budgetVariancePct,
      yoyDelta,
      yoyDeltaPct,
      revenuePerLot,
      grossMargin: g.grossMarginKnown ? g.grossMargin : null,
      grossMarginPct,
    };
  });
}

const ThCell = ({ label, testId }) => (
  <th data-testid={testId} className="text-right px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#737373]">{label}</th>
);

const SortableTh = ({ label, k, sortKey, sortDir, onClick, align = 'right', testId }) => {
  const active = sortKey === k;
  return (
    <th
      data-testid={testId}
      onClick={() => onClick(k)}
      className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:bg-[#F0F0F0] ${align === 'right' ? 'text-right' : 'text-left'} ${active ? 'text-[#171717]' : 'text-[#737373]'}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </span>
    </th>
  );
};

const SummaryCell = ({ label, value, sub }) => (
  <div>
    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#A3A3A3]">{label}</p>
    <p className="text-lg font-bold text-[#171717] font-heading mt-0.5">{value}</p>
    <p className="text-[10px] text-[#737373]">{sub}</p>
  </div>
);

const VarianceChip = ({ pctVal }) => {
  if (pctVal == null || Number.isNaN(pctVal)) return <span className="text-[#A3A3A3]">—</span>;
  const positive = pctVal >= 0;
  const tone = positive ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50';
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${tone}`}>
      <Icon className="w-3 h-3" />
      {positive ? '+' : ''}{pct(pctVal, 1)}
    </span>
  );
};

const MarginPctChip = ({ pctVal }) => {
  if (pctVal == null || Number.isNaN(pctVal)) return <span className="text-[#A3A3A3]">—</span>;
  const tone = pctVal >= 40 ? 'text-emerald-700 bg-emerald-50'
             : pctVal >= 20 ? 'text-sky-700 bg-sky-50'
             : pctVal >= 0  ? 'text-amber-700 bg-amber-50'
             : 'text-rose-700 bg-rose-50';
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${tone}`}>
      {pct(pctVal, 1)}
    </span>
  );
};

export default ClientLadder;

// Inline-editable Lots cell.  Click to edit → type new value → Enter saves
// AND jumps focus to the next row's Lots cell for rapid entry.  Escape
// aborts.  A lock icon indicates an override and offers a one-click clear.
const LotsCell = ({ row, rowIndex, onSave, onClear }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(Math.round(row.occupiableLots || 0)));
  const inputRef = React.useRef(null);
  const isOverride = row.lotsSource === 'override';

  React.useEffect(() => { setValue(String(Math.round(row.occupiableLots || 0))); }, [row.occupiableLots]);
  React.useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  const commit = (advance) => {
    const numeric = Number(String(value).replace(/[^\d.]/g, ''));
    setEditing(false);
    if (!Number.isFinite(numeric)) return;
    if (numeric === Math.round(row.occupiableLots || 0)) return;
    onSave(numeric);
    if (advance) {
      // Focus the next visible Lots cell to enable rapid multi-row entry.
      setTimeout(() => {
        const next = document.querySelector(`[data-lots-cell-index="${rowIndex + 1}"]`);
        if (next) next.click();
      }, 200);
    }
  };

  return (
    <td
      className={`px-3 py-1.5 text-right text-[#525252] cursor-pointer group relative ${isOverride ? 'bg-amber-50/60' : ''}`}
      data-lots-cell-index={rowIndex}
      onClick={() => !editing && setEditing(true)}
      data-testid={`lots-cell-${row.planNumber}`}
    >
      {editing ? (
        <input
          ref={inputRef}
          data-testid={`lots-input-${row.planNumber}`}
          type="number"
          min="0"
          className="w-16 px-1.5 py-0.5 border border-[#171717] rounded text-right font-mono text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#171717]"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); commit(true); }
            else if (e.key === 'Escape') { setEditing(false); setValue(String(Math.round(row.occupiableLots || 0))); }
          }}
          onBlur={() => commit(false)}
          onClick={(e) => e.stopPropagation()}
          autoFocus
        />
      ) : (
        <span className="inline-flex items-center gap-1 justify-end">
          {isOverride && (
            <span
              title={`Overridden ${humanAgo(row.lotsOverrideAt)}${row.lotsOverrideNote ? ' · ' + row.lotsOverrideNote : ''}${row.lotsOriginal != null ? ` · workbook: ${Math.round(row.lotsOriginal)}` : ''}`}
              className="text-amber-700"
            >
              <Lock className="w-3 h-3" />
            </span>
          )}
          <span className={`${isOverride ? 'font-semibold text-amber-900' : ''}`}>
            {row.occupiableLots ? Math.round(row.occupiableLots).toLocaleString() : '—'}
          </span>
          {isOverride && (
            <button
              data-testid={`lots-clear-${row.planNumber}`}
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5 text-[#A3A3A3] hover:text-rose-600"
              title="Clear override — revert to workbook value"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </span>
      )}
    </td>
  );
};

// Minimal ago-formatter for the tooltip.  Same shape as LiveIndicator but
// inline here to avoid an extra import.
function humanAgo(when) {
  if (!when) return '';
  const t = new Date(when).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Math.max(0, Date.now() - t);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const LadderUnmappedWarning = ({ count, revenue, plans, fy }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid="ladder-unmapped-warning"
      className="bg-orange-50 border border-orange-200 rounded-[10px] p-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-4 h-4 text-orange-700" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-orange-900">
            {count} plan{count === 1 ? '' : 's'} not on the master list — excluded from {fy} ladder
          </p>
          <p className="text-xs text-orange-800/80 mt-0.5">
            {revenue > 0
              ? `$${Math.round(revenue).toLocaleString('en-AU')} of workbook revenue is withheld until these plans are mapped.`
              : 'These plans have $0 revenue in the workbook today, but still need to be added to the Master List Configuration.'}
          </p>
        </div>
        <button
          data-testid="ladder-unmapped-toggle"
          onClick={() => setOpen(o => !o)}
          className="text-xs font-semibold px-3 py-1.5 rounded-md bg-white text-orange-800 border border-orange-200 hover:bg-orange-100 flex-shrink-0"
        >
          {open ? 'Hide' : 'View list'}
        </button>
      </div>
      {open && (
        <div className="mt-3 pt-3 border-t border-orange-200 max-h-[280px] overflow-y-auto" data-testid="ladder-unmapped-list">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-orange-800/70">
                <th className="text-left px-2 py-1.5">Plan</th>
                <th className="text-left px-2 py-1.5">Property</th>
                <th className="text-left px-2 py-1.5">Workbook Manager</th>
                <th className="text-right px-2 py-1.5">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {plans.map(p => (
                <tr key={p.planNumber} className="border-t border-orange-100">
                  <td className="px-2 py-1 font-mono font-semibold text-orange-900">{p.planNumber}</td>
                  <td className="px-2 py-1 text-orange-900/80 truncate max-w-[220px]">{p.propertyName || '—'}</td>
                  <td className="px-2 py-1 text-orange-900/80">{p.manager || '—'}</td>
                  <td className="px-2 py-1 text-right font-mono text-orange-900">
                    {p.total ? `$${Math.round(p.total).toLocaleString('en-AU')}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
